import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { showNotification } from '@mantine/notifications';
import { setImpersonationHeader } from '../../libs/api-client/utils/request';
import {
  AttestraConnectUser,
  confirmAccountSSO,
  createUserSSO,
  getAttestraConnectUserSilent,
  logout,
  me as iam,
} from '../../services/auth/request';
import {
  clearToken,
  getAccessToken,
  getAccessTokenSSO,
  getImpersonatingEmail,
  getLocalAttestraUser,
  getMe,
  getLocalRealUser,
  getLocalRealUserPath,
  getRefreshToken,
  removeImpersonation,
  setImpersonation,
  setLocalAttestraUser,
  setMe,
  setLocalRealUser,
  setLocalRealUserPath,
  setRequestAccessConfirmed,
  setToken,
} from '../../services/LocalStorageService';
import { HttpStatusCode, OmvqEstablishmentUserType, PUBLIC_PATHS, ROLE } from '../../types';
import { useIntlLight } from '../../libs/i18n';

export interface IUser {
  id: string;
  email: string;
  username: string;
  name: string;
  role: string;
  acceptedUseTerm: string | null;
  organization: { id: string };
  organizationDepartment: { id: string };
  userConsents: { authorized: boolean; id: string; consentType: string }[];
  omvqEstablishmentUserType: string | null;
  hasApprovedPermit: boolean | null;
}

export interface IValueAuthInterface {
  token: string | null;
  tokenSSO: string | null;
  loggedIn: boolean;
  hasUser: boolean;
  refresh_token: string | null;
  attestraUser: AttestraConnectUser | null;
  user: IUser | undefined;
  realUser: IUser | undefined;
  setUser: (value: React.SetStateAction<IUser | undefined>) => void;
  isImpersonating: boolean;
  enterImpersonation: (email: string) => void;
  exitImpersonation: () => void;
  isRoleDetenteurPermis: boolean;
  isRoleVeterinarian: boolean;
  isRoleSupport: boolean;
  isRoleSupportRo: boolean;
  isRoleProducer: boolean;
  isRoleAssociation: boolean;
  isRoleMapaq: boolean;
  isRoleMapaqRo: boolean;
  isRoleAdmin: boolean;
  isOmvqEstablishmentManager: boolean;
  isRoleVetSoftwareSupplier: boolean;
  hasApprovedPermit: boolean;
  isRoleMapaqOrAbove: (role: ROLE) => boolean;
  isRoleMapaqRoOrAbove: (role: ROLE) => boolean;
  isRoleAssociationOrAbove: (role: ROLE) => boolean;
  isRoleSupportOrAbove: (role: ROLE) => boolean;
  isRoleSupportRoOrAbove: (role: ROLE) => boolean;
  setToken(token: object): void;
  createUserSSO(user: any, url: string): Promise<any>;
  confirmSSO(confirm: any): Promise<any>;
  me(): Promise<any>;
  logout(): void;
  hasConsent(consent: string): boolean;
}

export const AuthContext = React.createContext<IValueAuthInterface>({} as IValueAuthInterface);

export const isRoleMapaqOrAbove = (role: ROLE) =>
  [ROLE.ROLE_MAPAQ, ROLE.ROLE_SUPPORT, ROLE.ROLE_ADMIN].includes(role);
export const isRoleMapaqRoOrAbove = (role: ROLE) =>
  [ROLE.ROLE_MAPAQ_RO, ROLE.ROLE_MAPAQ, ROLE.ROLE_SUPPORT, ROLE.ROLE_ADMIN].includes(role);
export const isRoleAssociationOrAbove = (role: ROLE) =>
  [ROLE.ROLE_ASSOCIATION, ROLE.ROLE_MAPAQ, ROLE.ROLE_SUPPORT, ROLE.ROLE_ADMIN].includes(role);
export const isRoleSupportOrAbove = (role: ROLE) =>
  [ROLE.ROLE_SUPPORT, ROLE.ROLE_ADMIN].includes(role);
export const isRoleSupportRoOrAbove = (role: ROLE) =>
  [ROLE.ROLE_SUPPORT_RO, ROLE.ROLE_SUPPORT, ROLE.ROLE_ADMIN].includes(role);

interface AuthSchemeProviderProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthSchemeProviderProps) => {
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(getImpersonatingEmail);
  const [user, setUser] = useState<IUser | undefined>(getMe);
  const [realUser, setRealUser] = useState<IUser | undefined>(getLocalRealUser);
  const [attestraConnectUser, setAttestraConnectUser] = useState<
    AttestraConnectUser | null | undefined
  >();
  const { t } = useIntlLight();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          onError: (error: any | unknown) => {
            // Les 401 sont gérés par l'intercepteur axios (refresh token) dans request.tsx
            if (error?.message === '401') {
              return;
            }
            showNotification({
              autoClose: false,
              title: t('message.notificationTitle'),
              message: `${error?.message}`,
              color: 'red',
            });
          },
        }),
      })
  );

  const enterImpersonation = useCallback(
    async (email: string) => {
      try {
        const currentUser = user || getMe();
        if (currentUser && !realUser) {
          setRealUser(currentUser);
          setLocalRealUser(currentUser);
        }
        const token = crypto.randomUUID();
        setImpersonation(email, token);
        setImpersonationHeader(email, token);
        const data = await iam();
        setUser(data);
        setImpersonatedEmail(email);
        await queryClient.cancelQueries();
        queryClient.removeQueries();
        setLocalRealUserPath(window.location.href);
        window.location.assign('/');
      } catch (error: any) {
        removeImpersonation();
        setImpersonationHeader(null, null);

        showNotification({
          autoClose: false,
          title: t('auth.errorImpersonation'),
          message: error?.message,
          color: 'red',
        });
      }
    },
    [queryClient, user, realUser, t]
  );

  const exitImpersonation = useCallback(async () => {
    try {
      removeImpersonation();
      setImpersonationHeader(null, null);

      if (realUser) {
        setUser(realUser);
        setImpersonatedEmail(null);
        queryClient.clear();
      } else {
        const data = await iam();
        setUser(data);
        setImpersonatedEmail(null);
        await queryClient.cancelQueries();
        queryClient.removeQueries();
      }
      window.location.assign(getLocalRealUserPath() ?? '/');
    } catch {
      window.location.href = '/logout';
    }
  }, [queryClient, realUser]);

  // Fetch de la session cognito
  useEffect(() => {
    if (attestraConnectUser === undefined) {
      const localAttestraUser = getLocalAttestraUser();
      if (localAttestraUser) {
        setAttestraConnectUser(localAttestraUser);
        return;
      }
      getAttestraConnectUserSilent().then(userInfo => {
        setAttestraConnectUser(userInfo);
        setLocalAttestraUser(userInfo);
      });
    }
  }, [attestraConnectUser]);

  useEffect(() => {
    if (realUser) {
      setLocalRealUser(realUser);
    }
  }, [realUser]);

  useEffect(() => {
    // Sur les pages publiques (login, logout, callbacks, erreurs), on skip
    // entièrement le check Cognito : aucun cookie de session attendu → un 401
    // sur /api/oauth2/user_info_attestra_connect est garanti et inutile.
    // /login_callback fait son propre appel /api/me dans LoginCallback.tsx.
    if (PUBLIC_PATHS.some(path => window.location.pathname.startsWith(path))) {
      return;
    }

    // En impersonation, le cookie access_token reste celui de l'utilisateur
    // original → Cognito retourne un 401 sur user_info_attestra_connect.
    // On skip ce check et on appelle directement /api/me qui respecte le
    // header X-Switch-User via le firewall Symfony.
    if (impersonatedEmail) {
      iam()
        .then(data => {
          setUser(data);
        })
        .catch((error: any) => {
          removeImpersonation();
          setImpersonatedEmail(null);

          showNotification({
            autoClose: false,
            title: t('auth.errorImpersonation'),
            message: error?.message,
            color: 'red',
          });
        });
      return;
    }

    // La session cognito a déjà été fetch et a retourné null
    if (attestraConnectUser === null) {
      return;
    }

    // Étape 1 : userInfo Cognito = porte d'entrée. Pas de session → on s'arrête,
    // pas de "Impersonation Error" parasite et pas de redirect vers /logout.
    // Étape 2 : si la session Cognito existe, on appelle /api/me pour distinguer
    //   - 200 : user inscrit dans cette app → on hydrate l'état
    //   - 428 : user Cognito sans user_account → notification d'inscription
    getAttestraConnectUserSilent()
      .then(userInfo => {
        if (!userInfo) {
          return undefined; // pas de session Cognito
        }

        return iam()
          .then(data => {
            setUser(data);
            const impersonating = getImpersonatingEmail();
            if (!impersonating && !realUser) {
              setRealUser(data);
            }
          })
          .catch((error: any) => {
            if (HttpStatusCode.ATTESTRA_USER_WITHOUT_ACCOUNT === error?.response?.status) {
              return;
            }
            if (
              HttpStatusCode.USER_NOT_FOUND === error?.response?.status &&
              error.response.data.detail === 'auth_failed_vet'
            ) {
              logout();
              return;
            }
            throw error;
          });
      })
      .catch((error: any) => {
        showNotification({
          autoClose: false,
          title: t('auth.errorAuthentication'),
          message: error?.message,
          color: 'red',
        });
      });
  }, [attestraConnectUser, impersonatedEmail, realUser, t]);

  useEffect(() => {
    if (user) setMe(user);
  }, [user]);

  const isImpersonating = !!impersonatedEmail;

  const value = useMemo<IValueAuthInterface>(
    () => ({
      token: getAccessToken(),
      tokenSSO: getAccessTokenSSO(),
      hasUser: user !== undefined,
      loggedIn: user !== undefined,
      refresh_token: getRefreshToken(),
      attestraUser: attestraConnectUser ?? null,
      user,
      realUser,
      setUser,
      isImpersonating,
      enterImpersonation,
      exitImpersonation,
      isRoleDetenteurPermis: user?.role === ROLE.ROLE_DETENTEUR_PERMIS,
      isRoleMapaq: user?.role === ROLE.ROLE_MAPAQ,
      isRoleMapaqRo: user?.role === ROLE.ROLE_MAPAQ_RO,
      isRoleAdmin: user?.role === ROLE.ROLE_ADMIN,
      isRoleVeterinarian: user?.role === ROLE.ROLE_VET,
      isRoleSupport: user?.role === ROLE.ROLE_SUPPORT,
      isRoleSupportRo: user?.role === ROLE.ROLE_SUPPORT_RO,
      isRoleProducer: user?.role === ROLE.ROLE_PRODUCER,
      isRoleAssociation: user?.role === ROLE.ROLE_ASSOCIATION,
      isOmvqEstablishmentManager:
        user?.omvqEstablishmentUserType === OmvqEstablishmentUserType.NOT_VET,
      isRoleVetSoftwareSupplier: user?.role === ROLE.ROLE_VET_SOFTWARE_SUPPLIER,
      hasApprovedPermit: Boolean(user?.hasApprovedPermit),
      isRoleMapaqOrAbove,
      isRoleMapaqRoOrAbove,
      isRoleAssociationOrAbove,
      isRoleSupportOrAbove,
      isRoleSupportRoOrAbove,
      setToken: (token: object) => {
        setToken(token);
      },
      createUserSSO: (newUser, url) => createUserSSO(newUser, url).then(response => response),
      confirmSSO: confirmSSO =>
        confirmAccountSSO(confirmSSO).then(result => {
          return result;
        }),
      me: (): Promise<any> => {
        return new Promise((resolve, reject) => {
          iam()
            .then(data => {
              setUser(data);
              if (!isImpersonating) {
                setRealUser(data);
              }
              resolve(data);
            })
            .catch(error => reject(error));
        });
      },
      logout: () => {
        removeImpersonation();
        setImpersonatedEmail(null);
        clearToken();
        setUser(undefined);
        setRealUser(undefined);
        setLocalRealUser(undefined);
        setAttestraConnectUser(undefined);
        setLocalAttestraUser(null);
        setRequestAccessConfirmed(false);
        logout();
      },
      hasConsent: (consent: string) => {
        return Boolean(
          user?.userConsents?.find(
            (userConsent: { consentType: string }) => userConsent?.consentType === consent
          )
        );
      },
    }),
    [user, attestraConnectUser, realUser, isImpersonating, enterImpersonation, exitImpersonation]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </QueryClientProvider>
  );
};

export default AuthProvider;
